// Project settings (v0.1.226): the one place a project is edited from inside it — web Settings tab, phone
// Project settings screen, the MCP's ops_update_project. Pure: validation for each field group, the department
// filter chip's counts on the Projects page, the default department of a new task, and the "apply the home
// department to the open tasks" pick. Writes go through commands.updateProject / moveTasksToDepartment.
import { GENERAL_DEPARTMENT_ID, listDepartments, departmentOf } from './hierarchy.js';
import { isProjectLive } from './projectstatus.js';
import { ALL_DEPARTMENTS, taskDepartmentResolver, projectDepartmentIndex, projectInDepartment, isDepartmentLocked } from './taskdepartments.js';

const DATE = /^\d{4}-\d{2}-\d{2}$/;

/** Where a new task in this project lands in department mode: the department you're in, or the project's home one. */
export const NEW_TASK_DEPARTMENT = [
  { key: 'current', label: 'The department you’re in', hint: 'New tasks go in the department picked on this device (General on All).' },
  { key: 'home', label: 'This project’s home department', hint: 'New tasks here go in the home department, whichever one you’re in.' },
];
export const newTaskDepartmentMode = (project) => (project?.newTaskDepartment === 'home' ? 'home' : 'current');

/**
 * The department to stamp on a new top-level task (TaskContext's withDept): an id, null (General), or undefined to
 * leave the task as it is. A project locked to a department always gives its own (over a template's, mode on or off).
 * Otherwise only in department mode, and never over a department the task already carries.
 */
export function newTaskDepartmentId(task, { project = null, departmentActive = false, currentDepartmentId = ALL_DEPARTMENTS } = {}) {
  if (!task || task.parentId) return undefined;
  if (isDepartmentLocked(project)) return project.departmentId && project.departmentId !== GENERAL_DEPARTMENT_ID ? project.departmentId : null;
  if (task.departmentId !== undefined || !departmentActive) return undefined;
  if (project && newTaskDepartmentMode(project) === 'home') {
    const id = project.departmentId;
    return id && id !== GENERAL_DEPARTMENT_ID ? id : undefined;
  }
  return currentDepartmentId && currentDepartmentId !== ALL_DEPARTMENTS && currentDepartmentId !== GENERAL_DEPARTMENT_ID ? currentDepartmentId : undefined;
}

/** "1,250.50" / "$85" / "" → number | null (blank) | NaN (not a number). */
export function parseAmount(v) {
  if (v === null || v === undefined) return null;
  if (typeof v === 'number') return Number.isFinite(v) ? v : NaN;
  const s = String(v).replace(/[$,\s]/g, '');
  if (!s) return null;
  return /^-?\d*\.?\d+$/.test(s) ? Number(s) : NaN;
}

/**
 * Check one field group before it's written. Returns { patch, errors } — errors keyed by field, patch = the
 * cleaned values (trimmed, numbers parsed, '' → null) to hand to commands.updateProject when errors is empty.
 * Dates are checked against each other with the project's other date when only one is given.
 */
export function checkProjectPatch(input = {}, project = {}) {
  const patch = {}; const errors = {};
  if ('name' in input) {
    const n = String(input.name ?? '').trim();
    if (!n) errors.name = 'A project needs a name.';
    else patch.name = n;
  }
  for (const k of ['description', 'address', 'icon', 'color']) if (k in input) patch[k] = String(input[k] ?? '').trim();
  if ('icon' in patch && !patch.icon) errors.icon = 'Pick an icon.';
  if ('color' in patch && !/^#[0-9a-f]{3,8}$/i.test(patch.color)) errors.color = 'Pick a colour.';
  for (const k of ['startDate', 'endDate']) {
    if (!(k in input)) continue;
    const d = input[k] ? String(input[k]).trim() : '';
    if (!d) patch[k] = null;
    else if (!DATE.test(d) || Number.isNaN(new Date(`${d}T12:00:00`).getTime())) errors[k] = 'Use a date like 2026-10-31.';
    else patch[k] = d;
  }
  const start = 'startDate' in patch ? patch.startDate : project.startDate || null;
  const end = 'endDate' in patch ? patch.endDate : project.endDate || null;
  if (!errors.startDate && !errors.endDate && start && end && end < start && ('startDate' in patch || 'endDate' in patch)) {
    errors['endDate' in input ? 'endDate' : 'startDate'] = 'The due date is before the start.';
  }
  for (const k of ['value', 'budget', 'hourlyRate']) {
    if (!(k in input)) continue;
    const n = parseAmount(input[k]);
    if (Number.isNaN(n)) errors[k] = 'Numbers only.';
    else if (n !== null && n < 0) errors[k] = 'Can’t be below zero.';
    else patch[k] = n === null ? 0 : Math.round(n * 100) / 100;
  }
  if ('billable' in input) patch.billable = input.billable === true || input.billable === false ? input.billable : null;
  if ('newTaskDepartment' in input) patch.newTaskDepartment = input.newTaskDepartment === 'home' ? 'home' : null;
  return { patch, errors };
}

/**
 * The Projects page's department chip: how many projects (passing `keep`, e.g. the status filter) each choice
 * shows. Department mode: a project is in every department one of its tasks is in (none yet → all of them);
 * off: the project's own department. Returns Map id → count, with ALL_DEPARTMENTS = the total.
 */
export function projectCountsByDepartment({ projects = [], tasks = [], departments = [], departmentMode = false, keep = () => true } = {}) {
  const list = listDepartments(departments);
  const pool = projects.filter((p) => p && keep(p));
  const out = new Map([[ALL_DEPARTMENTS, pool.length], ...list.map((d) => [d.id, 0])]);
  if (departmentMode) {
    const index = projectDepartmentIndex(tasks, taskDepartmentResolver(tasks, departments, projects));
    // A locked project lives in its department only, tasks or not.
    const inDept = (p, id) => (isDepartmentLocked(p) ? departmentOf(p, departments).id === id : projectInDepartment(p.id, id, index));
    for (const d of list) out.set(d.id, pool.filter((p) => inDept(p, d.id)).length);
  } else {
    for (const p of pool) { const id = departmentOf(p, departments).id; out.set(id, (out.get(id) || 0) + 1); }
  }
  return out;
}

/** Open top-level tasks of a project not already in `departmentId` — what "apply to all tasks" would move. */
export function tasksOffDepartment(tasks = [], projectId, departmentId, departments = []) {
  const target = departmentId || GENERAL_DEPARTMENT_ID;
  const deptOf = taskDepartmentResolver(tasks, departments);
  return tasks.filter((t) => t && t.projectId === projectId && !t.parentId && !t.deletedAt && t.status !== 'done' && deptOf(t) !== target).map((t) => t.id);
}

/**
 * A task in a locked project can't change department — it moves to another project instead. The projects it could
 * go to for `departmentId`: live, not this one, not locked elsewhere; homed in that department first, then by name.
 */
export function projectsForDepartmentMove(projects = [], departmentId, departments = [], excludeId = null) {
  const target = departmentId || GENERAL_DEPARTMENT_ID;
  return projects
    .filter((p) => p && p.id !== excludeId && isProjectLive(p) && (!isDepartmentLocked(p) || departmentOf(p, departments).id === target))
    .map((p) => ({ p, home: departmentOf(p, departments).id === target }))
    .sort((a, b) => (b.home - a.home) || String(a.p.name).localeCompare(String(b.p.name)))
    .map((x) => x.p);
}
